import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useCart } from "../cart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Copy, ArrowLeft } from "lucide-react";

const DISTRICTS = ["Dhaka","Chittagong","Rajshahi","Khulna","Sylhet","Barisal","Rangpur","Mymensingh","Comilla","Narayanganj","Gazipur","Bogura"];

export default function Checkout() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [settings, setSettings] = useState(null);
  const { getCart, clearCart } = useCart();
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_address: "", district: "Dhaka", payment_method: "cod", transaction_id: "", note: "" });
  const [placed, setPlaced] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/stores/public/${slug}`).then(r => setStore(r.data)).catch(() => navigate("/"));
    api.get("/settings").then(r => setSettings(r.data));
  }, [slug, navigate]);

  if (!store || !settings) return null;
  const cart = getCart(store.store_id);
  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const delivery = form.district === "Dhaka" ? settings.delivery_dhaka : settings.delivery_outside;
  const total = subtotal + delivery;

  const submit = async () => {
    if (!form.customer_name || !form.customer_phone || !form.customer_address) return toast.error("Please fill contact info");
    if ((form.payment_method === "bkash" || form.payment_method === "nagad") && !form.transaction_id) return toast.error("Enter Transaction ID");
    try {
      const r = await api.post("/orders", { store_id: store.store_id, items: cart, ...form });
      setPlaced(r.data);
      clearCart(store.store_id);
    } catch { toast.error("Order failed"); }
  };

  if (placed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-xl border border-slate-200" data-testid="order-success">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto"/>
          <h1 className="mt-4 text-2xl font-black">Order Placed!</h1>
          <p className="text-slate-500 mt-2 font-bn">আপনার অর্ডার সফলভাবে গ্রহণ করা হয়েছে</p>
          <div className="mt-4 p-3 bg-slate-50 rounded-lg font-mono font-bold">{placed.order_id}</div>
          <div className="mt-4 text-sm">Total <span className="font-black text-orange-600 font-mono text-lg">৳{placed.total}</span></div>
          <div className="mt-6 flex gap-2">
            <Link to={`/s/${slug}`} className="flex-1"><Button variant="outline" className="w-full">Continue Shopping</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  const bkash = store.bkash_number || settings.bkash_number;
  const nagad = store.nagad_number || settings.nagad_number;
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to={`/s/${slug}`} className="text-slate-500 hover:text-orange-500"><ArrowLeft className="w-5 h-5"/></Link>
          <div className="font-bold">Checkout · চেকআউট</div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200">
            <h3 className="font-bold mb-3">Delivery Details</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Full name</Label><Input value={form.customer_name} onChange={e=>setForm({...form, customer_name: e.target.value})} data-testid="checkout-name"/></div>
              <div><Label>Phone</Label><Input value={form.customer_phone} onChange={e=>setForm({...form, customer_phone: e.target.value})} placeholder="01xxxxxxxxx" data-testid="checkout-phone"/></div>
              <div className="sm:col-span-2"><Label>Address</Label><Textarea value={form.customer_address} onChange={e=>setForm({...form, customer_address: e.target.value})} rows={2} data-testid="checkout-address"/></div>
              <div>
                <Label>District</Label>
                <Select value={form.district} onValueChange={v=>setForm({...form, district: v})}>
                  <SelectTrigger data-testid="checkout-district"><SelectValue/></SelectTrigger>
                  <SelectContent>{DISTRICTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label>Note (optional)</Label><Input value={form.note} onChange={e=>setForm({...form, note: e.target.value})}/></div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200">
            <h3 className="font-bold mb-3">Payment Method</h3>
            <RadioGroup value={form.payment_method} onValueChange={v => setForm({...form, payment_method: v})}>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${form.payment_method === "cod" ? "border-emerald-400 bg-emerald-50" : "border-slate-200"}`} data-testid="pay-cod">
                <RadioGroupItem value="cod" className="mt-1"/>
                <div>
                  <div className="font-semibold">Cash on Delivery</div>
                  <div className="text-xs text-slate-500 font-bn">ডেলিভারির সময় নগদ পরিশোধ</div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${form.payment_method === "bkash" ? "border-pink-400 bg-pink-50" : "border-slate-200"}`} data-testid="pay-bkash">
                <RadioGroupItem value="bkash" className="mt-1"/>
                <div className="flex-1">
                  <div className="font-semibold text-pink-700">bKash</div>
                  <div className="text-xs text-slate-500 font-bn">Send Money করে TxID দিন</div>
                  {form.payment_method === "bkash" && (
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center gap-2 p-2 bg-white rounded border">
                        <span className="text-xs text-slate-500">Merchant:</span>
                        <span className="font-mono font-bold flex-1">{bkash}</span>
                        <button onClick={(e)=>{e.preventDefault(); copy(bkash);}} className="text-pink-600"><Copy className="w-4 h-4"/></button>
                      </div>
                      <Input placeholder="Transaction ID" value={form.transaction_id} onChange={e=>setForm({...form, transaction_id: e.target.value})} data-testid="bkash-txid"/>
                    </div>
                  )}
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${form.payment_method === "nagad" ? "border-orange-400 bg-orange-50" : "border-slate-200"}`} data-testid="pay-nagad">
                <RadioGroupItem value="nagad" className="mt-1"/>
                <div className="flex-1">
                  <div className="font-semibold text-orange-700">Nagad</div>
                  <div className="text-xs text-slate-500 font-bn">Send Money করে TxID দিন</div>
                  {form.payment_method === "nagad" && (
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center gap-2 p-2 bg-white rounded border">
                        <span className="text-xs text-slate-500">Merchant:</span>
                        <span className="font-mono font-bold flex-1">{nagad}</span>
                        <button onClick={(e)=>{e.preventDefault(); copy(nagad);}} className="text-orange-600"><Copy className="w-4 h-4"/></button>
                      </div>
                      <Input placeholder="Transaction ID" value={form.transaction_id} onChange={e=>setForm({...form, transaction_id: e.target.value})} data-testid="nagad-txid"/>
                    </div>
                  )}
                </div>
              </label>
            </RadioGroup>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 h-fit sticky top-4">
          <h3 className="font-bold mb-3">Order Summary</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {cart.map(i => (
              <div key={i.product_id} className="flex gap-2 text-sm">
                <span className="flex-1 truncate">{i.name} <span className="text-slate-400">x{i.quantity}</span></span>
                <span className="font-mono font-bold">৳{i.price * i.quantity}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">৳{subtotal}</span></div>
            <div className="flex justify-between"><span>Delivery</span><span className="font-mono">৳{delivery}</span></div>
            <div className="flex justify-between text-lg font-black pt-2 border-t"><span>Total</span><span className="font-mono text-orange-600">৳{total}</span></div>
          </div>
          <Button onClick={submit} disabled={cart.length === 0} className="w-full mt-4 bg-orange-500 hover:bg-orange-600 h-12 font-bold" data-testid="place-order-btn">Place Order</Button>
        </div>
      </div>
    </div>
  );
}
